/** Compact, expandable activity trail for live and completed agent work. */

import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Box, Collapse, IconButton, Typography} from '@mui/material'
import {
  CheckCircleOutline as CheckIcon,
  ErrorOutline as ErrorIcon,
  ExpandMore as ExpandIcon,
  RadioButtonUnchecked as PendingIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material'
import type {ProgressStepData} from './types'

interface Props {
  steps: ProgressStepData[]
  isStreaming?: boolean
}

export const formatActivityElapsed = (milliseconds: number) => {
  const seconds = Math.max(0, Math.floor(milliseconds / 1_000))
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

const StepStatusIcon: React.FC<{status: ProgressStepData['status']}> = ({status}) => {
  if (status === 'running') {
    return <Box sx={{width: 13, height: 13, border: '1.5px solid', borderColor: 'primary.main', borderTopColor: 'transparent', borderRadius: '50%', animation: 'rosActivitySpin 0.9s linear infinite', '@keyframes rosActivitySpin': {to: {transform: 'rotate(360deg)'}}}} />
  }
  if (status === 'complete') return <CheckIcon sx={{fontSize: 15, color: 'success.main'}} />
  if (status === 'warning') return <WarningIcon sx={{fontSize: 15, color: 'warning.main'}} />
  if (status === 'failed') return <ErrorIcon sx={{fontSize: 15, color: 'error.main'}} />
  return <PendingIcon sx={{fontSize: 14, color: 'text.disabled'}} />
}

const ProgressStepCard: React.FC<Props> = ({steps, isStreaming = false}) => {
  const fallbackStartedAt = useRef(Date.now())
  const [now, setNow] = useState(Date.now())
  const [expanded, setExpanded] = useState(isStreaming)

  useEffect(() => {
    // Activity that was visible while a turn ran should remain visible when the
    // terminal event arrives. Fast tools can otherwise appear for only a few
    // milliseconds and collapse before the researcher can read them. A card
    // mounted from restored/completed history still starts collapsed because
    // its initial isStreaming value is false.
    if (isStreaming) setExpanded(true)
    if (!isStreaming) return
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [isStreaming])

  const {startedAt, completedAt} = useMemo(() => {
    const starts = steps.map(step => step.startedAt).filter((value): value is number => typeof value === 'number')
    const completions = steps.map(step => step.completedAt).filter((value): value is number => typeof value === 'number')
    return {
      startedAt: starts.length ? Math.min(...starts) : fallbackStartedAt.current,
      completedAt: completions.length ? Math.max(...completions) : undefined,
    }
  }, [steps])

  if (!steps.length) return null
  const elapsed = Math.max(0, (isStreaming ? now : completedAt ?? now) - startedAt)

  return (
    <Box sx={{py: 0.5}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 30}}>
        <Typography sx={{fontSize: '0.78rem', color: 'text.secondary', whiteSpace: 'nowrap'}}>
          {isStreaming ? 'Working' : 'Worked'} for {formatActivityElapsed(elapsed)}
        </Typography>
        <Box sx={{height: '1px', bgcolor: 'divider', flex: 1}} />
        <IconButton size="small" aria-label={expanded ? 'Hide activity details' : 'Show activity details'} onClick={() => setExpanded(value => !value)} sx={{p: 0.25, color: 'text.disabled'}}>
          <ExpandIcon sx={{fontSize: 17, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms ease'}} />
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout={160}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.7, pt: 0.5, pb: 0.75}}>
          {steps.map(step => (
            <Box key={step.stepId} sx={{display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr) auto', columnGap: 0.75, alignItems: 'start', color: step.status === 'running' ? 'text.primary' : 'text.secondary'}}>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 20}}><StepStatusIcon status={step.status} /></Box>
              <Box sx={{minWidth: 0}}>
                <Typography sx={{fontSize: '0.8rem', lineHeight: 1.55, color: 'inherit'}}>{step.name}</Typography>
                {step.description && <Typography sx={{fontSize: '0.72rem', lineHeight: 1.45, color: 'text.disabled'}}>{step.description}</Typography>}
                {!!step.substeps?.length && (
                  <Box sx={{pt: 0.25}}>
                    {step.substeps.map((substep, index) => (
                      <Typography key={`${step.stepId}:${index}`} sx={{fontSize: '0.71rem', lineHeight: 1.45, color: 'text.disabled'}}>
                        {substep.status === 'complete' ? '✓' : substep.status === 'warning' ? '!' : '·'} {substep.name}{substep.result ? ` — ${substep.result}` : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
              {step.timing && <Typography sx={{fontSize: '0.68rem', lineHeight: '20px', color: 'text.disabled'}}>{step.timing}</Typography>}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

export default ProgressStepCard
